import * as React from "react"
import { Container, Row, Col, Table, Spinner, Button } from "react-bootstrap"
import { Subscription, BehaviorSubject, of, concat } from "rxjs"
import { distinctUntilChanged, switchMap, map } from "rxjs/operators"
import { toast } from "react-toastify"
import { format, parseISO } from "date-fns"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import TablePagination from "../TablePagination"
import ColumnHeaderFilter from "../ColumnHeaderFilter"
import { OrphanedGridGet, OrphanedDelete } from "../../api/OrphanedResults/OrphanedProcess"
import OrphanedGridResponse from "../../api/OrphanedResults/Models/OrphanedGridResponse"
import OrphanedRow from "../../api/OrphanedResults/Models/OrphanedRow"
import ConfirmationModal from "../ConfirmationModal"
import Checkbox from "../Checkbox"
import { Filter, Sort } from "../../api/GridRequest"
import { setOrphanedResultsCount } from "../../storage/LogInPortals"
import DonorNotFoundError from "./DonorNotFoundError/DonorNotFoundError"
import NoMatchingRandomError from "./NoMatchingRandomError/NoMatchingRandomError"
import NoMatchingTestError from "./NoMatchingTestError/NoMatchingTestError"
import AccountNotFoundError from "./AccountNotFoundError/AccountNotFoundError"
import { ImportExceptionSubType } from "../../api/OrphanedResults/Models/ImportExceptionSubType"
import ImageCCF from "../../assets/images/CCF.png"
import { RAImage } from "../../api/OrderResults/OrderResultModel/OrderResultRecord"
import ImageGet, { ImageData } from "../../api/OrderResults/GenerateCCFImage"

import FormatTimestampFileName from "../../formatters/FormatTimestampFileName"
import FormatData from "../../formatters/FormatBase64"
import HorizontalScrollWrapper from "../HorizontalScrollWrapper"
import { connect } from "react-redux"
import { AppState } from "../../AppState"
import GetDonorID, { GetDonorIDTitle } from "../../formatters/RenderDonorID"
import { GetSelectedPortal } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"

interface OrphanedResultsProps {
  unmaskDonorId: number
}

interface OrphanedResultsState {
  loading: boolean
  data?: OrphanedGridResponse
  selectAll: boolean
  selectedList: { TestDetailID: number | string, AccountID?: number, TestDetailClinicalID: number | string }[]
  confirmModal: boolean
  recordCount: number
  showDonorNotFoundError: boolean
  showNoMatchingRandomError: boolean
  showNoMatchingTestError: boolean
  showAccountNotFoundError: boolean
  errorRow?: OrphanedRow
}

interface OrphanedResultsSearchParams {
  pageNumber: number
  sort: Sort
  filters: Filter[]
}

const PageSize = 10

type OrphanedColumn = keyof OrphanedRow | "Checked"

const DefaultColumns: { name: OrphanedColumn, label: string }[] = [
  { name: "Checked", label: "" },
  { name: "DonorID", label: "Donor ID" },
  { name: "FirstName", label: "First Name" },
  { name: "LastName", label: "Last Name" },
  { name: "CompanyName", label: "Company" },
  { name: "Reason", label: "Reason" },
  { name: "ReferenceID", label: "Sample ID" },
  { name: "TestType", label: "Type" },
  { name: "DateCollected", label: "Collected" },
  { name: "RAImageID", label: "eDocs" },
  { name: "ImportException", label: "Error" }
]

export class OrphanedResults extends React.PureComponent<OrphanedResultsProps, OrphanedResultsState> {
  state: OrphanedResultsState = {
    loading: false,
    selectAll: false,
    selectedList: [],
    confirmModal: false,
    recordCount: 0,
    showDonorNotFoundError: false,
    showNoMatchingRandomError: false,
    showNoMatchingTestError: false,
    showAccountNotFoundError: false,
  }

  gridRef = React.createRef<HTMLTableElement>()

  page$ = new BehaviorSubject<OrphanedResultsSearchParams>({
    pageNumber: 1,
    sort: { column: "", order: "" },
    filters: []
  })
  pageSubscription?: Subscription
  actionsSubscription?: Subscription
  loadImageSubscription?: Subscription

  componentDidMount() {
    //this.recordCountGet()
    this.pageSubscription = this.page$.pipe(
      distinctUntilChanged(),
      switchMap(params => concat(
        of({ loading: true }),
        OrphanedGridGet(params.pageNumber, PageSize, params.sort, params.filters).pipe(
          map((result: OrphanedGridResponse) => ({ data: result, loading: false })))
      ))
    ).subscribe(
      (newState: { loading: boolean, data?: OrphanedGridResponse }) => {
        if (newState.data) {
          setOrphanedResultsCount(newState.data.Total)
          this.setState({ loading: newState.loading, data: newState.data })
        } else {
          setOrphanedResultsCount(0)
          this.setState({ loading: newState.loading, selectedList: [], selectAll: false })
        }
      },
      (error: string) => {
        this.setState({ loading: false })
        toast.error(error)
      }
    )
  }

  componentWillUnmount() {
    this.pageSubscription && this.pageSubscription.unsubscribe()
    this.actionsSubscription && this.actionsSubscription.unsubscribe()
    this.loadImageSubscription && this.loadImageSubscription.unsubscribe()
    this.page$.unsubscribe()
  }

  pageChanged = (pageNumber: number) => {
    this.page$.next({ ...this.page$.value, pageNumber: pageNumber })
  }

  sortGrid(column: string, order?: string) {
    const newSort = { column: column, order: order || "" }
    this.page$.next({ ...this.page$.value, sort: newSort })
  }

  filterGrid(column: string, items: string[]) {
    const filters = [...this.page$.value.filters]
    const filter = { column: column, values: items }
    let index = filters.findIndex(item => item.column === filter.column)
    if (index >= 0) {
      filters[index] = filter
    } else {
      filters.push(filter)
    }

    this.page$.next({ ...this.page$.value, pageNumber: 1, filters: filters })
  }

  convertDate = (date?: string) => {
    if (date) {
      return format(parseISO(date), "MM/dd/yyyy")
    } else {
      return ""
    }
  }

  handleAllTestDetailCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    const tests = checked && this.state.data ? this.state.data.Records.map((row: OrphanedRow) => ({ TestDetailID: row.TestDetailID, TestDetailClinicalID: row.TestDetailClinicalID })) : []
    this.setState({ selectedList: tests, selectAll: checked })
  }

  handleTestDetailCheck = (e: React.ChangeEvent<HTMLInputElement>, isClinical: boolean) => {
    const value = e.target.value
    let tests = [...this.state.selectedList]

    const index = tests.findIndex(test => isClinical ? test.TestDetailClinicalID === value : test.TestDetailID === value)
    if (index > -1) {
      tests = [...tests.slice(0, index), ...tests.slice(index + 1)]
    } else {
      tests.push({ TestDetailID: (isClinical ? 0 : value), TestDetailClinicalID: (isClinical ? value : 0) })
    }
    this.setState({ selectedList: tests })
  }


  handleCancel = () => {
    //TODO
  }

  handleDelete = () => {
    this.setState({ confirmModal: true })
  }

  hideModal = () => {
    this.setState({ confirmModal: false })
  }

  confirmDelete = () => {
    this.hideModal()
    const deleteRecords = this.state.selectedList.map(item => {
      return { TestDetailID: item.TestDetailID, TestDetailClinicalID: item.TestDetailClinicalID }
    })
    this.actionsSubscription = OrphanedDelete(deleteRecords).subscribe(
      data => {
        toast.success(data)
        this.page$.next({ ...this.page$.value, pageNumber: 1 })
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error, stateChanges: object) => {
    this.setState(stateChanges)
    toast.error(error)
  }

  renderColumns = () => {
    return DefaultColumns.map((col) => {
      if (col.name === "Checked") {
        return <th className="text-center font-weight-normal" key={col.name}>
          <Checkbox id="check_head" label={col.label} onChange={this.handleAllTestDetailCheck} checked={this.state.selectAll} />
        </th>
      }
      const sort = this.page$.value.sort
      return (
        <th className="text-nowrap font-weight-normal" key={col.name}>
          {this.state.data && col.name in this.state.data && <ColumnHeaderFilter
            title={col.label}
            sort={sort.column === col.name ? sort.order === "asc" ? "asc" : "desc" : undefined}
            items={this.state.data[col.name]}
            onSort={(order?: string) => this.sortGrid(col.name, order)}
            onFilter={(items: string[]) => this.filterGrid(col.name, items)}
          />}
          {this.state.data && !(col.name in this.state.data) && (col.label?.toUpperCase())}
        </th>
      )
    })
  }

  testDetailSelected = (id: string, clinicalId: string, isClinical: boolean) => {
    return this.state.selectedList.findIndex(test => (isClinical ? test.TestDetailClinicalID === clinicalId : test.TestDetailID === id)) > -1
  }

  renderCell = (row: OrphanedRow, colName: OrphanedColumn) => {
    switch (colName) {
      case "Checked":
        return <td className="text-center" key={row.IsClinical ? row.TestDetailClinicalID : row.TestDetailID}>
          <Checkbox id={`check_${row.TestDetailID}_${row.TestDetailClinicalID}`} label="" value={row.IsClinical ? row.TestDetailClinicalID.toString() : row.TestDetailID.toString()} checked={this.testDetailSelected(row.TestDetailID, row.TestDetailClinicalID, row.IsClinical)} onChange={(e) => this.handleTestDetailCheck(e, row.IsClinical)} />
        </td>
      case "DateCollected":
        return <td key={`${colName}_${row.TestDetailID}`}>{this.convertDate(row.DateCollected)}</td>
      case "RAImageID":
        const iconsVisibility = this.iconsVisibility(row)
        return <td key={`ra_image_${row.TestDetailID}_${row.TestDetailClinicalID}`} className="align-text-top" style={{ whiteSpace: "nowrap" }}>
          {iconsVisibility ? this.mapCCFImages(row) : ""}
        </td>

      case "ImportException":
        const linkVisible = this.isShowErrorDialog(row)
        return <td key={`${colName}_${row.TestDetailID}`}>
          {linkVisible ? <Button variant="underline-link" onClick={() => this.showErrorDialog(row)}>{this.renderException(row)}</Button> : row.ImportException}
        </td>
      case "DonorID":
        const idValue = row.DonorID || ""// GetDonorID(row.DonorID, row.MockDonorID, this.props.unmaskDonorId)
        const idTitle = GetDonorIDTitle(row.DonorID, row.MockDonorID, this.props.unmaskDonorId)
        return <td key={`${colName}_${row.TestDetailID}`} title={idTitle}>{idValue}</td>
      default:
        return <td key={`${colName}_${row.TestDetailID}`}>{row[colName]}</td>
    }
  }

  renderException = (row: OrphanedRow) => {
    const { ImportExceptionSubTypeID, ReasonCode, ImportException } = row;

    if (
      (ImportExceptionSubTypeID === ImportExceptionSubType.TestDoesNotExist ||
      ImportExceptionSubTypeID === ImportExceptionSubType.MoreThanOneTestMatch) &&
      ReasonCode?.toUpperCase() === "RAN"
    ) {
      return "No Matching Random";
    }

    return ImportException;
  }

  renderRows = () => {
    if (this.state.data && this.state.data.Records.length) {
      return this.state.data.Records.map((row: OrphanedRow, i: number) =>
        <tr key={i}>
          {DefaultColumns.map((col) => this.renderCell(row, col.name))}
        </tr>)
    } else {
      return <tr><td colSpan={DefaultColumns.length}>No Records Found</td></tr>
    }
  }

  refreshGrid = () => {
    this.page$.next({ ...this.page$.value, pageNumber: 1 })
  }

  onCloseDonorNotFoundError = (withUpdate: boolean) => {
    this.setState({ showDonorNotFoundError: false }, () => { if (withUpdate) this.refreshGrid })
    this.refreshGrid()
  }

  onCloseAccountNotFoundError = (withUpdate: boolean) => {
    this.setState({ showAccountNotFoundError: false }, () => { if (withUpdate) this.refreshGrid })
    this.refreshGrid()
  }

  onCloseNoMatchingRandomError = () => {
    this.setState({ showNoMatchingRandomError: false }, this.refreshGrid)
    this.refreshGrid()
  }

  onCloseNoMatchingTestError = () => {
    this.setState({ showNoMatchingTestError: false }, this.refreshGrid)
    this.refreshGrid()
  }

  isShowDonorNotFoundError = (row: OrphanedRow): boolean => {
    if (!row.ImportException) {
      return false
    }
    const importExceptionSubType = row.ImportExceptionSubTypeID
    if ((importExceptionSubType === ImportExceptionSubType.DonorDoesNotExist) ||
      (importExceptionSubType === ImportExceptionSubType.MoreThanOneDonorMatch)) {
      return true
    }
    return false
  }

  isShowNoMatchingRandomError = (row: OrphanedRow): boolean => {
    if (!row.ImportException) {
      return false
    }
    const importExceptionSubType = row.ImportExceptionSubTypeID
    if ((importExceptionSubType === ImportExceptionSubType.TestDoesNotExist) ||
      (importExceptionSubType === ImportExceptionSubType.MoreThanOneTestMatch)) {
      if (row.ReasonCode?.toUpperCase() === "RAN") {
        return true
      }
    }
    return false
  }
  isShowNoMatchingTestError = (row: OrphanedRow): boolean => {
    if (!row.ImportException) {
      return false
    }
    const importExceptionSubType = row.ImportExceptionSubTypeID
    if ((importExceptionSubType === ImportExceptionSubType.TestDoesNotExist) ||
      (importExceptionSubType === ImportExceptionSubType.MoreThanOneTestMatch)) {
      if (row.ReasonCode?.toUpperCase() !== "RAN") {
        return true
      }

    }
    return false
  }
  isShowAccountNotFoundError = (row: OrphanedRow): boolean => {
    if (!row.ImportException) {
      return false
    }
    const importExceptionSubType = row.ImportExceptionSubTypeID
    if ((importExceptionSubType === ImportExceptionSubType.LabAccountNotFoundorNotAssignedtoaCompany) ||
      (importExceptionSubType === ImportExceptionSubType.LabAccountAssignedMorethanOneCompany)) {
      return true
    }
    return false

  }
  isShowErrorDialog = (row: OrphanedRow): boolean => {
    if (!row.ImportException) {
      return false
    }
    if (row.ImportExceptionSubTypeID === 0) {
      return false
    }
    if (this.isShowDonorNotFoundError(row)) {
      return true
    }
    if (this.isShowNoMatchingRandomError(row)) {
      return true
    }
    if (this.isShowNoMatchingTestError(row)) {
      return true
    }
    if (this.isShowAccountNotFoundError(row)) {
      return true
    }
    return false
  }

  showErrorDialog = (row: OrphanedRow) => {
    if (!this.isShowErrorDialog(row)) {
      this.setState({
        showDonorNotFoundError: false,
        showNoMatchingRandomError: false,
        showNoMatchingTestError: false,
        showAccountNotFoundError: false,
        errorRow: undefined
      })
      return
    }

    if (this.isShowDonorNotFoundError(row)) {
      this.setState({
        showDonorNotFoundError: true,
        showNoMatchingRandomError: false,
        showNoMatchingTestError: false,
        showAccountNotFoundError: false,
        errorRow: row
      })
      return
    }
    if (this.isShowNoMatchingRandomError(row)) {
      this.setState({
        showDonorNotFoundError: false,
        showNoMatchingRandomError: true,
        showNoMatchingTestError: false,
        showAccountNotFoundError: false,
        errorRow: row
      })
      return
    }
    if (this.isShowNoMatchingTestError(row)) {
      this.setState({
        showDonorNotFoundError: false,
        showNoMatchingRandomError: false,
        showNoMatchingTestError: true,
        showAccountNotFoundError: false,
        errorRow: row
      })
      return
    }
    if (this.isShowAccountNotFoundError(row)) {
      this.setState({
        showDonorNotFoundError: false,
        showNoMatchingRandomError: false,
        showNoMatchingTestError: false,
        showAccountNotFoundError: true,
        errorRow: row
      })
      return
    }
  }
  iconsVisibility = (row: OrphanedRow): boolean => {
    if (!row) {
      return false
    }
    if (!Array.isArray(row.RAImage)) {
      return false
    }
    if (row.RAImage.length === 0) {
      return false
    }
    return true
  }

  /*
    renderRows = () => {
    if (this.state.data && this.state.data.Records.length) {
      return this.state.data.Records.map((row: OrphanedRow, i: number) =>
        <tr key={i}>
          {DefaultColumns.map((col) => this.renderCell(row, col.name))}
        </tr>)
    } else {
      return <tr><td colSpan={DefaultColumns.length}>No Records Found</td></tr>
    }
  }

  */
  mapCCFImages = (row: OrphanedRow) => {
    const imgSrc = ImageCCF
    if (!Array.isArray(row.RAImage)) {
      return ""
    }

    return row.RAImage.map((item: RAImage, i: number) => 
      <span> 
      {this.mapCCFImage(row, item, i)}
      </span>
    )
      
    //return this.mapCCFImage(row, row.RAImage[0], 0)
  }



  mapCCFImage = (row: OrphanedRow, item: RAImage, i: number) => {
    const imgSrc = ImageCCF
    const space = i > 0 ? "0.5em" : "0px"

    return <img key={'ra_image_' + item.RAImageID} onClick={() => this.generateCCFImage(item, row)} src={imgSrc} alt={this.ccfImageDescription(row, item)} title={this.ccfImageDescription(row, item)} style={{marginLeft:space}}/>
  }

  ccfImageDescription = (row: OrphanedRow, item: RAImage) => {
    return item.Description
  }

  generateCCFImage = (image: RAImage, row: OrphanedRow) => {
    this.loadImageSubscription && this.loadImageSubscription.unsubscribe()
    const data = {
      TestImageID: image.RAImageID,
      TestDetailID: row.TestDetailID,
      TestDetailClinicalID: row.TestDetailClinicalID || "",
      IsMarketPlace: true,
      TestDescription: image.Description
    }

    this.loadImageSubscription = ImageGet(data).subscribe(
      (data: ImageData) => this.processCCF(data),
      (error) => this.handleAPIError(error, {})
    )
  }

  processCCF = (data: ImageData) => {
    if (data && Array.isArray(data.Base64String)) {
      if (data.Base64String.length == 0) {
        toast.error("Service is not able to return CCF image for this record.")
        return
      }
      const currentDate = new Date()
      data.Base64String.forEach((item, index) => {
        const type = item.ImageType
        const fileName = FormatTimestampFileName("CCF_Image_Report" + index, type.split('/')[1], currentDate)
        const fileData = new Blob([FormatData(item.Base64Strings)], { type: type || data.ImageType })
        var fileURL = URL.createObjectURL(fileData)
        window.open(fileURL)
      })
    } else {
      toast.error("Service is not able to return CCF image for this record.")
    }
  }

  render() {
    const {
      errorRow,
      showDonorNotFoundError,
      showNoMatchingRandomError,
      showNoMatchingTestError,
      showAccountNotFoundError,
    } = this.state

    return (
      <AppPageWrapper parentClassName={"align-items-center"} showNav>
        <Container fluid className="grid-container pt-2">
          <Row>
            <Col className="px-0">
              <HorizontalScrollWrapper
              gridRef={this.gridRef}
              visible={this.state.data && this.state.data?.Total > 0}
              recalculateWidth={!this.state.loading}
              offsetVertical={330}
              >
                <Table bordered className="mb-0" ref={this.gridRef}>
                  <thead>
                    <tr>
                      {this.renderColumns()}
                    </tr>
                  </thead>
                  <tbody>
                    {
                      this.state.loading ?
                        <tr>
                          <td className="align-middle text-center" colSpan={DefaultColumns.length}>
                            <div className="d-flex justify-content-center text-secondary" style={{ width: "100vw" }}>
                                <Spinner animation="border" variant="secondary" role="status" className="mr-2" />
                                <span>Loading...</span>
                            </div>
                          </td>
                        </tr> : this.renderRows()
                    }
                  </tbody>
                </Table>
              </HorizontalScrollWrapper>
            </Col>
          </Row>
          {this.state.data &&
            <Row>
              <Col className="px-0">
                <span>Total records: {this.state.data.Total}</span>
                <TablePagination pageNumber={this.page$.value.pageNumber} pages={this.state.data.Pages} onChange={this.pageChanged} />
              </Col>
            </Row>}
          <Row>
            <Col className="text-right mb-2 px-0">
              <Button variant="secondary" onClick={this.handleCancel} className="mr-2">
                Cancel
              </Button>
              <Button variant="primary" onClick={this.handleDelete} disabled={this.state.selectedList.length === 0} className="mr-2">
                Delete
              </Button>
              <ConfirmationModal show={this.state.confirmModal}
                title="CONFIRM REMOVE"
                textTitle="Are you sure you want to remove selected record?"
                text="Selected record will be removed permanently."
                onConfirm={this.confirmDelete}
                onHide={this.hideModal} />
            </Col>
          </Row>
          {errorRow && showDonorNotFoundError && <DonorNotFoundError errorRow={errorRow} onClose={this.onCloseDonorNotFoundError} />}
          {errorRow && showNoMatchingRandomError && <NoMatchingRandomError errorRow={errorRow} onClose={this.onCloseNoMatchingRandomError} />}
          {errorRow && showNoMatchingTestError && <NoMatchingTestError errorRow={errorRow} onClose={this.onCloseNoMatchingTestError} />}
          {errorRow && showAccountNotFoundError && <AccountNotFoundError errorRow={errorRow} onClose={this.onCloseAccountNotFoundError} />}
        </Container>
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): OrphanedResultsProps {
  const portal = GetSelectedPortal(state)
  const unmaskDonorId = portal ? portal.UnmaskDonorID : 0
  return {
    unmaskDonorId: unmaskDonorId
  }
}
const ConnectedOrphanedResults = connect(mapStateToProps)(OrphanedResults)
export default ConnectedOrphanedResults
